import { useCallback, useRef, useState } from 'react';
import { markAbilitiesRevealedForInstance } from '../ability_display/AbilitySymbolAnimation';
import { coalesceArranAbilityStealFlyBatch } from './coalesceArranAbilityStealFlyBatch';
function mergeGrantQueue(prev, grants) {
  if (!grants?.length) {
    return prev;
  }
  const existingKeys = new Set(prev.map((g) => g.key));
  const merged = [...prev];
  grants.forEach((grant) => {
    if (!existingKeys.has(grant.key)) {
      merged.push(grant);
    }
  });
  return merged;
}

/**
 * Server-authoritative Arr'an (389) ability steal fly animations.
 */
export const useArranAbilityStealFlyAnimations = () => {
  const [activeFlyGrants, setActiveFlyGrants] = useState([]);
  const consumedFlyAnimationKeysRef = useRef(new Set());

  const queueGrants = useCallback((grants) => {
    if (!grants?.length) {
      return;
    }

    const grantsToQueue = [];
    grants.forEach((grant) => {
      if (!grant?.key) {
        throw new Error('useArranAbilityStealFlyAnimations.queueGrants: grant key is required');
      }
      if (consumedFlyAnimationKeysRef.current.has(grant.key)) {
        return;
      }
      consumedFlyAnimationKeysRef.current.add(grant.key);
      grantsToQueue.push(grant);
    });

    const revealedByArranInstance = new Map();
    grantsToQueue.forEach((grant) => {
      if (!grant.arranInstanceId || !grant.abilityName) {
        throw new Error('useArranAbilityStealFlyAnimations.queueGrants: arranInstanceId and abilityName are required');
      }
      const sid = String(grant.arranInstanceId);
      if (!revealedByArranInstance.has(sid)) {
        revealedByArranInstance.set(sid, []);
      }
      revealedByArranInstance.get(sid).push(grant.abilityName);
    });
    revealedByArranInstance.forEach((abilities, arranInstanceId) => {
      markAbilitiesRevealedForInstance(arranInstanceId, abilities);
    });

    const coalescedGrants = coalesceArranAbilityStealFlyBatch(grantsToQueue);
    coalescedGrants.forEach((coalescedGrant) => {
      consumedFlyAnimationKeysRef.current.add(coalescedGrant.key);
    });

    if (coalescedGrants.length === 0) {
      return;
    }

    setActiveFlyGrants((prev) => mergeGrantQueue(prev, coalescedGrants));
  }, []);

  const dismissFlyGrant = useCallback((key) => {
    setActiveFlyGrants((prev) => prev.filter((g) => g.key !== key));
  }, []);

  return {
    activeFlyGrants,
    dismissFlyGrant,
    queueGrants,
  };
};

export default useArranAbilityStealFlyAnimations;
